import React from 'react';
import { Document, Font, View, Text, Page, StyleSheet, Link } from '@react-pdf/renderer';

import _ from 'lodash';

import getLogoDimensionsForView from '../../utils/getLogoDimensionsForView';
import { OrganizationBrandData } from '../../../../_libs/app/types';
import { ReportDataType } from '../../../../_libs/data/types';

import DataBarGraphPrintSummary from '../../../../molecules/print-summary/DataBarGraphPrintSummary';
import DataStatPrintSummary from '../../../../molecules/print-summary/DataStatPrintSummary';
import DataBlurbPrintSummary from '../../../../molecules/print-summary/DataBlurbPrintSummary';
import ValuePropPrintSummary from '../../../../molecules/print-summary/ValuePropPrintSummary';

import PdfCropView from '../../../../organisms/PdfCropView';

import Theme from '../../../../../src/models/theme';
import PdfImageFromAzureWithOptions from '../../../../atoms/PdfImageFromAzureWithOptions';
import PdfImageFromAzureWithUri from '../../../../atoms/PdfImageFromAzureWithUri';

const PDF_BLEED = 4.5; // 0.125in bleed required by Las Colinas Print
const PDF_WIDTH = 792 + PDF_BLEED * 2;
const PDF_HEIGHT = 612 + PDF_BLEED * 2;

// Remove hyphenation
Font.registerHyphenationCallback(
  (word): string[] => { return [word]; }
);

interface FeaturesLogicCountProp {
  featureLogicId: number;
  count: number;
}
interface FeaturesCountProp {
  featureId: number;
  count: number;
  featureLogics: Record<number, FeaturesLogicCountProp>;
}
interface ThemesCountProp {
  themeId: number;
  count: number;
  features: Record<number, FeaturesCountProp>;
  prevalenceCount: number;
}

const Styles = StyleSheet.create({
  body: {
    fontFamily: 'Helvetica',
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    position: 'relative',
  },
  bold: {
    fontFamily: 'Helvetica-Bold',
  },
  emphasis: {
    fontFamily: 'Times-Bold',
    fontSize: '11pt',
    lineHeight: 0.5,
  },
  copy: {
    fontSize: '10pt',
    lineHeight: 1.3,
  },
  copySmall: {
    fontSize: '9pt',
    lineHeight: 1.33,
  },
  headline: {
    fontFamily: 'Helvetica-Bold',
    fontSize: '13pt',
    lineHeight: 1.3,
    textTransform: 'uppercase',
  },
  subheadline: {
    fontFamily: 'Helvetica-Bold',
    fontSize: '10pt',
    lineHeight: 1.3,
    textTransform: 'uppercase',
  },
  statLarge: {
    fontFamily: 'Helvetica-Bold',
    fontSize: '42pt',
    lineHeight: 1,
  },
  stat: {
    fontFamily: 'Helvetica-Bold',
    fontSize: '32pt',
    lineHeight: 1,
  },
  statSmall: {
    fontFamily: 'Helvetica-Bold',
    fontSize: '24pt',
    lineHeight: 1,
  },
  page: {
    color: 'white',
    fontSize: 10,
    lineHeight: 1.35,
  },
  bodyEmphasis: {
    fontFamily: 'Helvetica-Bold',
  },
  column: {
    width: 'calc(100%/3)',
  },
  bgView: {
    position: 'absolute',
    top: PDF_BLEED,
    left: PDF_BLEED,
  },
  bgImage: {
    width: '792px',
    height: '612px',
  },
  verticallyCenterContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
});

const Blurb = ({
  imageFileName,
  color,
  headlineTop,
  headlineBottom,
  description,
}): JSX.Element => {
  return (
    <View style={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    }}>
      <View style={{
        marginRight: 12,
      }}>
        <PdfImageFromAzureWithOptions
          fileName={imageFileName}
          folderPath="assets//"
          style={{
            width: 144,
            height: 120,
          }}
        />
      </View>
      <View style={{
        width: 300,
      }}>
        <Text style={{
          color,
          ...Styles.subheadline,
        }}>{headlineTop}</Text>
        <Text style={{
          marginBottom: 6,
          color,
          ...Styles.subheadline,
        }}>{headlineBottom}</Text>
        <Text style={
          Styles.copy
        }>{description}</Text>
      </View>
    </View>
  );
};

interface ClinicalGuideStatsProps {
  recommendations: number;
  vulnerabilities: number;
}

const ReportSummaryDoubleSided = ({
  coBrand,
  lbBrand,
  primaryBrand,
  clinicalGuideStats,
  radius,
  reportData,
  topThemes,
  themeData,
  zipCode,
}: {
  coBrand?: OrganizationBrandData;
  lbBrand: OrganizationBrandData;
  primaryBrand: OrganizationBrandData;
  clinicalGuideStats: ClinicalGuideStatsProps | false;
  radius: number;
  reportData: ReportDataType;
  topThemes: ThemesCountProp[];
  themeData: Theme[];
  zipCode: string;
}): JSX.Element => {

  const hasThemes = Object.keys(topThemes).length > 0;

  const logoUrl = primaryBrand.logoUrl; 
  const logoWidth = primaryBrand.logoWidth;
  const logoHeight = primaryBrand.logoHeight;
  const logoStyleView = {
    width: 125,
  };
  const logoDimensions = getLogoDimensionsForView({ 
    width: logoWidth,
    height: logoHeight,
  }, {
    width: logoStyleView.width,
    height: 60,
  });

  const _coBrand = (coBrand) ? coBrand : lbBrand;
  const coBrandLogoUrl = _coBrand.logoUrl;
  const coBrandLogoWidth = _coBrand.logoWidth;
  const coBrandLogoHeight = _coBrand.logoHeight;
  const coBrandStyleView = {
    width: 60,
  };
  const coBrandDimensions = getLogoDimensionsForView({
    width: coBrandLogoWidth,
    height: coBrandLogoHeight,
  }, { 
    width: coBrandStyleView.width, 
    height: 60,
  });

  const lbBrandDimensions = getLogoDimensionsForView({
    width: lbBrand.logoWidth,
    height: lbBrand.logoHeight,
  }, {
    width: 144,
    height: 54,
  });

  return (
    <Document>
      <Page
        style={{
          ...Styles.page,
          ...Styles.copy,
          backgroundColor: primaryBrand.colors.dark,
        }}
        size={{
          width: PDF_WIDTH, 
          height: PDF_HEIGHT, // Add 0.125in, or 9pt, bleed
        }}
      >
        <View style={Styles.bgView}>
          <PdfImageFromAzureWithOptions
            fileName="bg_summary.png"
            folderPath="assets/pdf_graphics/"
            style={Styles.bgImage}
          />
        </View>
        
        <View style={Styles.body}>
          
          {/* Basic statistics */}
          <View style={{
            ...Styles.column,
            padding: 36,
            backgroundColor: 'white',
            color: primaryBrand.colors.dark,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}>
            <View>
              {/* <Text>Generated For</Text> */}
              <View style={{
                width: 210,
                height: 60,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                alignContent: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
              >
                <View
                  style={{
                    height: logoDimensions.height,
                    ...logoStyleView
                  }}
                >
                  <PdfImageFromAzureWithUri
                    fileUri={logoUrl}
                    style={{
                      width: logoDimensions.width,
                      height: logoDimensions.height,
                    }}
                  />
                </View>
                <View
                  style={{
                    height: coBrandDimensions.height,
                    ...coBrandStyleView
                  }}
                >
                  <PdfImageFromAzureWithUri
                    fileUri={coBrandLogoUrl}
                    style={{
                      width: coBrandDimensions.width,
                      height: coBrandDimensions.height,
                    }}
                  />
                </View>
              </View>
              <Text>
                This is a summary of <Text style={ Styles.emphasis }>{reportData.count.toLocaleString('en-US')}</Text> block { reportData.count === 1 ? 'group' : 'groups'} within <Text style={ Styles.emphasis }>{radius}-miles</Text> of ZIP code <Text style={ Styles.emphasis }>{zipCode}</Text>'s center.</Text>
            </View>
            <View>
              <Text style={ Styles.subheadline }>Estimated Population</Text>
              <Text
                style={{
                  ...Styles.stat,
                  color: primaryBrand.colors.primary,
                }}
              >
                {(reportData.totalpopulation_est).toLocaleString('en-US')}
              </Text>
            </View>
            <View>
              <Text style={{
                ...Styles.subheadline,
                marginBottom: 4,
              }}>Providers within {radius} miles</Text>
              <DataBarGraphPrintSummary
                label="Family Medicine"
                value={reportData.NPIs.familyMedicine}
                percent={reportData.NPIs.familyMedicine/reportData.NPIsMax*100}
                primaryBrand={primaryBrand}
              />
              <DataBarGraphPrintSummary
                label="General Practice"
                value={reportData.NPIs.generalPractice}
                percent={reportData.NPIs.generalPractice/reportData.NPIsMax*100}
                primaryBrand={primaryBrand}
              />
              <DataBarGraphPrintSummary
                label="Internal Medicine"
                value={reportData.NPIs.internalMedicine}
                percent={reportData.NPIs.internalMedicine/reportData.NPIsMax*100}
                primaryBrand={primaryBrand}
              />
              <DataBarGraphPrintSummary
                label="Pediatricians"
                value={reportData.NPIs.pediatricians}
                percent={reportData.NPIs.pediatricians/reportData.NPIsMax*100}
                primaryBrand={primaryBrand}
              />
              <DataBarGraphPrintSummary
                label="Psychiatrists/Neurologists"
                value={reportData.NPIs.psychiatryNeurology}
                percent={reportData.NPIs.psychiatryNeurology/reportData.NPIsMax*100}
                primaryBrand={primaryBrand}
              />
              <DataBarGraphPrintSummary
                label="Psychologists"
                value={reportData.NPIs.psychologist}
                percent={reportData.NPIs.psychologist/reportData.NPIsMax*100}
                primaryBrand={primaryBrand}
              />
            </View>
            <View>
              <Text style={{
                ...Styles.subheadline,
                marginBottom: 4,
              }}>Screening Rates</Text>
              <View style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: 6.25,
              }}>
                {/* National rates from 2020 via https://gis.cdc.gov/Cancer/USCS/?CDC_AA_refVal=https%3A%2F%2Fwww.cdc.gov%2Fcancer%2Fdataviz%2Findex.htm#/CancerScreening/ */}
                <DataStatPrintSummary value={`${reportData.plcr_mammouse_value.toFixed(2)}%`} label="Breast Cancer" description="vs. 78.2% nat'l avg." primaryBrand={primaryBrand} />
                <DataStatPrintSummary value={`${reportData.plcr_colon_screen_value.toFixed(2)}%`} label="Colorectal Cancer" description="vs. 71.8% nat'l avg." primaryBrand={primaryBrand} />
              </View>
              <DataStatPrintSummary value={`${reportData.plcr_cervical_value.toFixed(2)}%`} label="Cervical Cancer" description="vs. 78.0% nat'l avg." primaryBrand={primaryBrand} />
            </View>
            <View style={{
              ...Styles.copySmall,
              ...Styles.verticallyCenterContent,
              height: 54,
              borderRadius: 6,
              border: `1px solid ${primaryBrand.colors.primary}`,
              padding: 6,
            }}>
              <Text>
                <Text style={{
                  fontFamily: 'Helvetica-Bold'
                }}>Get the full report.</Text> <Text>See community demographics, health literacy, poverty rates, uninsured rates, walkability, and more...</Text>
              </Text>
            </View>
          </View>
          
          {/* Statistics column */}
          <View style={{
            ...Styles.column,
            paddingHorizontal: 24,
            paddingVertical: 36,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <View style={{
              paddingLeft: 12,
              height: 60,
              ...Styles.headline,
              ...Styles.verticallyCenterContent,
            }}>
              <Text>AI-Identified Opportunity</Text>
              <Text>Within the Area</Text>
            </View>
            <View style={{
              height: 420,
              justifyContent: 'space-between',
            }}>
              {
                (!topThemes || !hasThemes) &&
                  <View
                    style={{
                      backgroundColor: 'black',
                      borderRadius: 6,
                      height: 120,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: 12,
                    }}
                  >
                    <Text>No AI insights were generated for the {radius}-mile radius from the center of {zipCode}. This usually happens when the location identified is sparsely populated.</Text>
                    <Text>Contact <Link style={{ color: 'white' }} src="mailto:info@lightbeamhealth.com"><Text>info@lightbeamhealth.com</Text></Link> to generate Radian&trade; reports for other areas for free.</Text>
                  </View>
              }
              {
                (topThemes && topThemes[0]) &&
                  <DataBlurbPrintSummary 
                    rank={1}
                    themeName={themeData[topThemes[0].themeId].name}
                    prevalence={`${(topThemes[0].prevalenceCount/reportData.count*100).toFixed(2)}%`}
                    n={topThemes[0].prevalenceCount}
                  />
              }
              {
                (topThemes && topThemes[1]) &&
                  <DataBlurbPrintSummary 
                    rank={2}
                    themeName={themeData[topThemes[1].themeId].name}
                    prevalence={`${(topThemes[1].prevalenceCount/reportData.count*100).toFixed(2)}%`}
                    n={topThemes[1].prevalenceCount}
                  />
              }
              {
                (topThemes && topThemes[2]) &&
                  <DataBlurbPrintSummary 
                    rank={3}
                    themeName={themeData[topThemes[2].themeId].name}
                    prevalence={`${(topThemes[2].prevalenceCount/reportData.count*100).toFixed(2)}%`}
                    n={topThemes[2].prevalenceCount}
                  />
              }
            </View>
            {
              coBrand &&
                <View style={{
                  paddingTop: 9,
                  paddingLeft: 12,
                  height: 60,
                  ...Styles.verticallyCenterContent,
                }}>
                  <View
                    style={Styles.copySmall}
                  >
                    <Text style={Styles.bodyEmphasis}>Members get more with {coBrand.name}.</Text>
                    <Text>View up to six insights in your full reports.</Text>
                  </View>
                </View>
            }
            </View>

          {/* Info column */}
          <View style={{
            ...Styles.column,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'black',
            paddingVertical: 36,
            paddingHorizontal: 36,
          }}>
            <View>
              <View style={{
                height: 54,
                marginBottom: 9,
                ...Styles.headline,
                ...Styles.verticallyCenterContent,
              }}>
                <Text>Understanding The</Text>
                <Text>Opportunity in Your Area</Text>
              </View>
              <Text style={{
                marginBottom: 9,
              }}>
                Each block group analyzed for this summary has unique vulnerabilities ranked by Lightbeam’s Health Equity AI. The top vulnerabilities from each block group were aggregated to create these insights.
              </Text>
              {
                hasThemes &&
                  <Text>
                    That means <Text style={Styles.emphasis}>{(topThemes[0].prevalenceCount/reportData.count*100).toFixed(2)}%</Text> of the <Text style={Styles.emphasis}>{reportData.count}</Text> block groups rank <Text style={Styles.emphasis}>{themeData[topThemes[0].themeId].name}</Text> as a key vulnerability–or a key opportunity–to&nbsp;address.
                  </Text>
              }
            </View>
            <View>
              <Text style={Styles.subheadline}>Clinical Recommendations Included in Full Report</Text>
              <PdfImageFromAzureWithOptions
                fileName="clinical_guides.png"
                folderPath="assets/pdf_graphics/"
                style={{
                  marginTop: 12,
                  width: 192,
                  height: 81,
                }}
              />
            </View>
            <View>
              {
                (hasThemes && clinicalGuideStats)
                  ?
                    <>
                      <ValuePropPrintSummary stat={clinicalGuideStats.vulnerabilities} statSize="lg" description={`specific vulnerabilities identified across ${Object.keys(topThemes).length} themes`} borderColor={primaryBrand.colors.primary} />
                      <ValuePropPrintSummary stat={clinicalGuideStats.recommendations} statSize="lg" description="evidence-based recommendations" borderColor={primaryBrand.colors.primary} />
                    </>
                  :
                    <>
                      <ValuePropPrintSummary stat="150+" statSize="md" description="vulnerabilities that may be identified across up to 6 themes" borderColor={primaryBrand.colors.primary} />
                      <ValuePropPrintSummary stat="1.3k" statSize="md" description="evidence-based recommendations to draw from" borderColor={primaryBrand.colors.primary} />
                    </>
              }
              <View style={{
                height: 60,
                borderTop: `1px solid ${primaryBrand.colors.primary}`,
                ...Styles.copySmall,
                ...Styles.verticallyCenterContent,
              }}>
                <Text style={ Styles.bodyEmphasis }>Curious about other locations?</Text>
                <Text>Contact <Link style={{ color: 'white' }} src="mailto:info@lightbeamhealth.com"><Text>info@lightbeamhealth.com</Text></Link> to generate Radian&trade; reports for other areas–at no cost.</Text>
              </View>
            </View>
          </View>
        </View>

        <PdfCropView
          width={PDF_WIDTH}
          height={PDF_HEIGHT}
          markLength={PDF_BLEED}
        />
      </Page>
      <Page
        style={{
          ...Styles.page, 
          backgroundColor: primaryBrand.colors.dark,
        }}
        size={{
          width: PDF_WIDTH, // Add 0.125in, or 9pt, bleed
          height: PDF_HEIGHT, // Add 0.125in, or 9pt, bleed
        }}
      >
        <View style={{
          backgroundColor: '#0974bc',
          width: '100%',
          height: '50%',
          position: 'absolute',
          bottom: 0,
          left: 0,
        }}>
        </View>
        <View style={Styles.bgView}>
          <PdfImageFromAzureWithOptions
            fileName="bg_summary.png"
            folderPath="assets/pdf_graphics/"
            style={Styles.bgImage}
          />
        </View>
        <View style={Styles.body}>
          <View style={{
            paddingHorizontal: 36,
            paddingVertical: 36,
            width: 'calc(100%/3)',
          }}>
            <View style={{
              height: 54,
              marginBottom: 6,
              ...Styles.verticallyCenterContent,
            }}>
              <Text style={Styles.headline}>Why use radian?</Text> 
            </View>
            <View style={{
              height: '50%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <ValuePropPrintSummary borderColor={primaryBrand.colors.primary} stat="3x" statWidth={61.25} description="more granular than other products, including CDC and SVI" descriptionWidth={155} />
              <ValuePropPrintSummary borderColor={primaryBrand.colors.primary} stat="1.3k" statWidth={61.25} description="evidence-based recommendations to draw from" descriptionWidth={155} />
              <ValuePropPrintSummary borderColor={primaryBrand.colors.primary} stat="10.5k" statWidth={61.25} description="possible variations of community-facing materials" descriptionWidth={155} />
            </View>
            <View style={{
              height: '50%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <View style={{
                height: 60,
                ...Styles.verticallyCenterContent,
              }}>
                <Text style={Styles.headline}>Why use lightbeam?</Text>
              </View>
              <ValuePropPrintSummary borderColor="#00ADEF" stat="$2.25B" statWidth={78} description="value-based care savings generated since 2014" />
              <View style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}>
                <View style={{
                  width: 90,
                }}>
                  <ValuePropPrintSummary borderColor="#00ADEF" stat="10x" statSize="sm" description="increase in patient reach" />
                  <ValuePropPrintSummary borderColor="#00ADEF" stat="15%" statSize="sm" description="reduction in avoidable readmissions" />
                </View>
                <View style={{
                  width: 90,
                  textAlign: 'center',
                }}>
                  <PdfImageFromAzureWithOptions
                    fileName="amga23_qr_code.png"
                    folderPath="assets/pdf_graphics"
                    style={{
                      marginBottom: 7,
                    }}
                  />
                  <Text style={Styles.copySmall}>Scan to learn more.</Text>
                </View>
              </View>
            </View>
          </View>
          <View style={{
            width: 'calc(200%/3)',
            backgroundColor: 'white',
            color: 'black',
            paddingHorizontal: 36,
            paddingVertical: 36,
          }}>
            <View style={{
              height: 54,
              marginBottom: 6,
              ...Styles.headline,
              ...Styles.verticallyCenterContent
            }}>
              <Text>Take the First Step Toward Health Equity Today.</Text>
              <Text>Everyone Will Benefit Tomorrow.</Text>
            </View>
            <View style={{
              display: 'flex',
              flexDirection: 'column',
              height: 420,
              justifyContent: 'space-between',
            }}>
              <Blurb
                imageFileName="lb_aco_reach.png"
                color="#00ADEF"
                headlineTop="ACO REACH and"
                headlineBottom="Health equity analytics"
                description = "Leverage HIE, EHR, Claims, ADT, and other data sources to provide the most accurate insights for your population."
                />
              <Blurb
                imageFileName="cs_sdoh.png"
                color="#EB8B0A"
                headlineTop="Deviceless RPM&trade;"
                headlineBottom="for Social Determinants"
                description = "Reach the right patient at the right time by harnessing patient- or member-reported outcomes to track potential health equity barriers via text message or phone call."
                />
              <Blurb
                imageFileName="jv_individual.png"
                color="#5410C2"
                headlineTop="Individual-Level"
                headlineBottom="Health Equity AI Models"
                description = "Apply patient or member-specific predictive models to uncover the risk factors and prescriptive recommendations for everyone you serve."
                />
            </View>
            <View style={{
              height: 54,
              marginTop: 6,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}>
              <View style={{
                marginRight: 12,
              }}>
                <PdfImageFromAzureWithUri
                  fileUri={lbBrand.logoUrl}
                  style={{
                    width: lbBrandDimensions.width,
                    height: lbBrandDimensions.height,
                  }}
                />
              </View>
              <View
                style={{
                  width: 300,
                  ...Styles.copySmall,
                }}
              >
                <Text>To learn more about Lightbeam’s health equity solutions,</Text>
                <Text>sign up for a demo at <Link style={{ color: '#0974bc' }} src="https://bit.ly/radian-amga23"><Text>https://bit.ly/radian-amga23</Text></Link>.</Text>                
              </View>
            </View>
            <View
              style={{
                position: 'absolute',
                bottom: 18 + PDF_BLEED,
                right: 18 + PDF_BLEED,
                fontFamily: 'Courier',
                fontSize: 8,
                color: 'black',
                opacity: 0.35,
              }}
            >
              <Text>
                Generated for {primaryBrand.name}.
              </Text>
            </View>
          </View>
        </View>
        <PdfCropView
          width={PDF_WIDTH}
          height={PDF_HEIGHT}
          markLength={PDF_BLEED}
        />
      </Page>
    </Document>
  );
};

export default ReportSummaryDoubleSided;
